import { ArrowUpRight } from "lucide-react";
import { useT, useI18n } from "@/lib/i18n";

const IMG = {
  maschinenbau:
    "https://images.unsplash.com/photo-1581092335397-9583eb92d232?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  automotive:
    "https://images.unsplash.com/photo-1567789884554-0b844b597180?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  bau:
    "https://images.unsplash.com/photo-1565043666747-69f6646db940?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  industrie:
    "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

const COPY = {
  de: {
    intro:
      "Unsere Verfahren veredeln Bauteile, die in der Schweiz und weit darüber hinaus tagtäglich Verantwortung tragen — vom kleinsten Verbinder bis zu architektonisch sichtbaren Edelstahlflächen.",
    fields: [
      { id: "maschinenbau", label: "Maschinenbau", body: "Funktions- und Korrosionsschutz für Komponenten in Antriebs-, Hydraulik- und Fertigungsanlagen." },
      { id: "automotive", label: "Automotive · Zulieferer", body: "Zink-Nickel und phosphatierte Oberflächen für Bauteile, die täglich Millionen von Lastwechseln aushalten." },
      { id: "bau", label: "Bau · Architektur", body: "Edelstahl-Finishing, geschliffene und elektropolierte Oberflächen für sichtbare Konstruktionen und Fassaden." },
      { id: "industrie", label: "Industrie · Apparatebau", body: "Reinraumtaugliche, hygienische und langlebige Oberflächen für Lebensmittel-, Pharma- und Anlagenbau." },
    ],
  },
  en: {
    intro:
      "Our finishes are applied to parts that carry responsibility every day — in Switzerland and far beyond — from the smallest connector to architecturally visible stainless-steel surfaces.",
    fields: [
      { id: "maschinenbau", label: "Mechanical engineering", body: "Functional and corrosion protection for components in drives, hydraulics and production equipment." },
      { id: "automotive", label: "Automotive · suppliers", body: "Zinc-nickel and phosphated surfaces for parts that endure millions of load cycles every day." },
      { id: "bau", label: "Construction · architecture", body: "Stainless-steel finishing, ground and electropolished surfaces for visible structures and façades." },
      { id: "industrie", label: "Industry · equipment", body: "Cleanroom-grade, hygienic and durable surfaces for food, pharma and process plant engineering." },
    ],
  },
  fr: {
    intro:
      "Nos traitements s'appliquent à des pièces qui assument une responsabilité au quotidien — en Suisse et bien au-delà — du plus petit connecteur jusqu'aux surfaces inox architecturalement visibles.",
    fields: [
      { id: "maschinenbau", label: "Construction mécanique", body: "Protection fonctionnelle et anti-corrosion pour composants d'entraînements, d'hydraulique et de machines de production." },
      { id: "automotive", label: "Automobile · sous-traitance", body: "Surfaces zinc-nickel et phosphatées pour pièces soumises à des millions de cycles de charge chaque jour." },
      { id: "bau", label: "Bâtiment · architecture", body: "Finition inox, surfaces brossées et polies électrolytiquement pour structures et façades visibles." },
      { id: "industrie", label: "Industrie · équipements", body: "Surfaces hygiéniques et durables, compatibles salles blanches, pour l'agroalimentaire, la pharmacie et les procédés industriels." },
    ],
  },
  it: {
    intro:
      "I nostri trattamenti vengono applicati a componenti che ogni giorno svolgono un ruolo di responsabilità — in Svizzera e ben oltre — dal più piccolo connettore alle superfici in acciaio inox visibili in architettura.",
    fields: [
      { id: "maschinenbau", label: "Costruzione meccanica", body: "Protezione funzionale e anticorrosiva per componenti di azionamenti, idraulica e impianti di produzione." },
      { id: "automotive", label: "Automotive · fornitori", body: "Superfici zinco-nichel e fosfatate per componenti che ogni giorno sopportano milioni di cicli di carico." },
      { id: "bau", label: "Edilizia · architettura", body: "Finitura inox, superfici levigate ed elettrolucidate per strutture e facciate a vista." },
      { id: "industrie", label: "Industria · impiantistica", body: "Superfici igieniche e durature, idonee a camere bianche, per alimentare, farmaceutico e impiantistica di processo." },
    ],
  },
};

export const Industries = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  return (
    <section
      id="anwendungen"
      data-testid="industries-section"
      className="relative bg-[#0E0E10] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="overline reveal mb-5">{t("sections.anwendungen_overline", "— Anwendungsbereiche")} · 04 / 07</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.anwendungen_title_1", "Wo unsere")}
              <br />
              <span className="text-zinc-500">{t("sections.anwendungen_title_2", "Oberflächen arbeiten.")}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-xl">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {c.fields.map((it, i) => (
            <article
              key={it.id}
              data-testid={`industry-card-${it.id}`}
              className={`reveal reveal-delay-${(i % 4) + 1} group relative h-[420px] md:h-[480px] bg-[#0E0E10] overflow-hidden`}
            >
              <img
                src={IMG[it.id]}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-[1400ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/70 to-transparent" />
              <div className="absolute inset-0 bg-black/30" />
              <div className="relative h-full p-8 md:p-12 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="overline text-zinc-300">0{i + 1}</span>
                  <div className="w-10 h-10 border border-white/30 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors duration-500">
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                </div>
                <div>
                  <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4">
                    {it.label}
                  </h3>
                  <p className="text-zinc-300 text-sm md:text-base leading-relaxed max-w-md">
                    {it.body}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Industries;
